import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { Clock, CheckCircle2 } from "lucide-react";

const TIMES = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

export default function Booking() {
    const location = useLocation();
    const [services, setServices] = useState([]);
    const [date, setDate] = useState();
    const [time, setTime] = useState("");
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        union_name: "",
        service_slug: location.state?.service || "",
        notes: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get("/services").then((r) => {
            setServices(r.data);
            if (!form.service_slug && r.data.length) {
                setForm((f) => ({ ...f, service_slug: r.data[0].slug }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!date) return toast.error("Pick a date");
        if (!time) return toast.error("Pick a time");
        setLoading(true);
        try {
            await api.post("/bookings", {
                ...form,
                preferred_date: format(date, "yyyy-MM-dd"),
                preferred_time: time,
            });
            setDone(true);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="booking-success">
                <div className="max-w-xl text-center">
                    <CheckCircle2 size={64} className="text-gold-500 mx-auto" />
                    <h1 className="mt-8 font-display text-4xl md:text-5xl font-black">Consultation requested.</h1>
                    <p className="mt-4 text-white/70">
                        We'll confirm your slot at {time} on {format(date, "PPP")} within one
                        business day at {form.email}.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-6xl px-6 md:px-12 py-20" data-testid="booking-page">
            <div className="label-tag">Book a consultation</div>
            <h1 className="mt-4 font-display text-5xl md:text-6xl font-black tracking-tight">
                A 30-minute call. No obligation.
            </h1>
            <p className="mt-6 text-white/70 max-w-2xl text-lg">
                Pick a date, pick a time, tell us what you're facing. We'll confirm by email.
            </p>

            <form onSubmit={submit} className="mt-14 grid md:grid-cols-12 gap-10" data-testid="booking-form">
                {/* Calendar & time */}
                <div className="md:col-span-5 space-y-6">
                    <div className="bg-ink-800 border border-white/10 rounded-2xl p-6">
                        <div className="label-tag mb-4">Select date</div>
                        <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            disabled={(d) => d < new Date(new Date().toDateString()) || d.getDay() === 0 || d.getDay() === 6}
                            className="text-white"
                            data-testid="booking-calendar"
                        />
                    </div>
                    <div className="bg-ink-800 border border-white/10 rounded-2xl p-6">
                        <div className="label-tag mb-4 flex items-center gap-2"><Clock size={14} /> Select time</div>
                        <div className="grid grid-cols-3 gap-2">
                            {TIMES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    data-testid={`time-slot-${t.replace(/[^0-9]/g, "")}`}
                                    onClick={() => setTime(t)}
                                    className={`px-3 py-2 rounded-full text-xs font-semibold tracking-wider transition-all border ${
                                        time === t
                                            ? "bg-gold-500 text-ink-900 border-gold-500"
                                            : "bg-ink-900 text-white/80 border-white/15 hover:border-gold-500/60"
                                    }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Form */}
                <div className="md:col-span-7 bg-ink-800 border border-white/10 rounded-2xl p-8 space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                        <Field label="Full name" required>
                            <input required data-testid="booking-name" value={form.name} onChange={upd("name")} className="bk-input" />
                        </Field>
                        <Field label="Email" required>
                            <input type="email" required data-testid="booking-email" value={form.email} onChange={upd("email")} className="bk-input" />
                        </Field>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        <Field label="Phone">
                            <input data-testid="booking-phone" value={form.phone} onChange={upd("phone")} className="bk-input" />
                        </Field>
                        <Field label="Union / Local">
                            <input data-testid="booking-union" value={form.union_name} onChange={upd("union_name")} className="bk-input" />
                        </Field>
                    </div>
                    <Field label="Service" required>
                        <select required data-testid="booking-service" value={form.service_slug} onChange={upd("service_slug")} className="bk-input">
                            {services.map((s) => (
                                <option key={s.slug} value={s.slug} className="bg-ink-900">
                                    {s.title}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Tell us what's happening">
                        <textarea rows={4} data-testid="booking-notes" value={form.notes} onChange={upd("notes")} className="bk-input resize-none" />
                    </Field>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="booking-submit-btn"
                        className="w-full px-7 py-4 rounded-full bg-gold-500 text-ink-900 font-bold uppercase tracking-wider text-sm hover:bg-gold-400 disabled:opacity-60"
                    >
                        {loading ? "Submitting…" : "Request Consultation"}
                    </button>
                </div>
            </form>
            <style>{`
                .bk-input {
                    width: 100%;
                    background: #0A0A0A;
                    border: 1px solid rgba(255,255,255,0.12);
                    border-radius: 10px;
                    padding: 12px 14px;
                    color: #fff;
                    font-size: 0.95rem;
                }
            `}</style>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-xs tracking-[0.2em] uppercase text-white/60 mb-2">
                {label}{required ? " *" : ""}
            </span>
            {children}
        </label>
    );
}
